import { nanoid } from 'nanoid'
import { makeAutoObservable } from 'mobx'
import { BUILD_VERSION } from 'shared/config'
import { type CallStore } from 'entities/Call'
import { sentEventSentry } from 'entities/Sentry'

// Worker TTL is 5 minutes; in foreground we ping comfortably below that.
// Background tabs get setInterval throttled (~1 min in Chrome, longer in
// energy-saver). visibilitychange/pageshow listeners below send an extra
// heartbeat the moment the tab returns, so even a heavily throttled tick
// is not the only line of defense.
const HEARTBEAT_INTERVAL_MS = 30_000

/**
 * Messages this client sends to the SharedWorker.
 * Discriminated union over `event`.
 */
type ISharedWorkerCallStoreClientMessage =
  | { event: 'register_tab'; tabId: string }
  | { event: 'heartbeat'; tabId: string }
  | { event: 'tab_busy'; tabId: string; busy: boolean }
  | { event: 'incoming_call'; tabId: string; callSid: string }
  | { event: 'call_accepted'; tabId: string; callSid: string }
  | { event: 'call_ended'; tabId: string; callSid: string }
  | { event: 'tab_closing'; tabId: string }

/**
 * Messages this client receives from the SharedWorker.
 * The worker always wraps the payload as `{ event, data, timestamp }`.
 */
type ISharedWorkerCallStoreServerMessage =
  | { event: 'set_ringing'; data: { callSid: string }; timestamp?: number }
  | { event: 'stop_ringing'; data: { callSid: string }; timestamp?: number }

const isServerMessage = (value: unknown): value is ISharedWorkerCallStoreServerMessage => {
  if (!value || typeof value !== 'object') return false
  const event = (value as { event?: unknown }).event
  if (event !== 'set_ringing' && event !== 'stop_ringing') return false
  const data = (value as { data?: unknown }).data
  if (!data || typeof data !== 'object') return false
  const callSid = (data as { callSid?: unknown }).callSid
  return typeof callSid === 'string' && callSid.length > 0
}

export class SharedWorkerCallStore {
  private readonly _tabId = nanoid(7)
  private _worker: SharedWorker | null = null
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null

  constructor(private _callStore: CallStore) {
    makeAutoObservable(this)

    this._init()
  }

  private _init = () => {
    if (typeof SharedWorker !== 'function') {
      console.warn('SharedWorker is not supported in this browser')
      return
    }

    if (this._worker) return

    this._worker = new SharedWorker(`/shared_worker_call.js?v=${BUILD_VERSION}`)
    this._worker.onerror = (e) => {
      sentEventSentry({
        event: '[SharedWorker] Failed to load',
        error: e,
        name: 'calling',
        level: 'error',
        sampleRate: 1.0,
      })
    }
    this._worker.port.start()
    this._initEvents()

    this._post({ event: 'register_tab', tabId: this._tabId })

    this._heartbeatTimer = setInterval(this._sendHeartbeat, HEARTBEAT_INTERVAL_MS)

    window.addEventListener('beforeunload', this._onBeforeUnload)
    document.addEventListener('visibilitychange', this._onVisibilityChange)
    // pageshow covers BFCache restore — beforeunload doesn't fire when the
    // page is parked into the back/forward cache, so on return the tab can
    // already be evicted from the worker's _tabs map.
    window.addEventListener('pageshow', this._onPageShow)
  }

  private _sendHeartbeat = () => {
    this._post({ event: 'heartbeat', tabId: this._tabId })
  }

  private _onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this._sendHeartbeat()
    }
  }

  private _onPageShow = () => {
    // Re-register on every show. If the tab survived in BFCache, the worker
    // may have already swept it; register_tab + heartbeat together restore
    // both the entry in _tabs and the lastSeenAt timestamp.
    this._post({ event: 'register_tab', tabId: this._tabId })
  }

  private _initEvents = () => {
    if (!this._worker) return

    this._worker.port.onmessage = (e: MessageEvent) => {
      if (!isServerMessage(e.data)) return

      switch (e.data.event) {
        case 'set_ringing':
          sentEventSentry({
            event: '[SharedWorker] set_ringing received',
            name: 'calling',
            level: 'log',
            tags: { callSid: e.data.data.callSid },
            sampleRate: 1.0,
          })
          this._callStore.startRing(e.data.data.callSid)
          return
        case 'stop_ringing':
          this._callStore.stopRing(e.data.data.callSid)
          return
      }
    }
  }

  private _onBeforeUnload = () => {
    this._post({ event: 'tab_closing', tabId: this._tabId })
  }

  private _post = (msg: ISharedWorkerCallStoreClientMessage) => {
    if (!this._worker) return
    try {
      this._worker.port.postMessage(msg)
    } catch (_) {
      // port may be detached during shutdown; ignore
    }
  }

  // --- public API used by CallStore ---

  notifyIncomingCall = (callSid: string) => {
    this._post({ event: 'incoming_call', tabId: this._tabId, callSid })
  }

  notifyCallAccepted = (callSid: string) => {
    this._post({ event: 'call_accepted', tabId: this._tabId, callSid })
  }

  notifyCallEnded = (callSid: string) => {
    this._post({ event: 'call_ended', tabId: this._tabId, callSid })
  }

  setBusy = (busy: boolean) => {
    this._post({ event: 'tab_busy', tabId: this._tabId, busy })
  }

  get isWorkerActive() {
    return this._worker !== null
  }

  dispose = () => {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer)
      this._heartbeatTimer = null
    }
    window.removeEventListener('beforeunload', this._onBeforeUnload)
    document.removeEventListener('visibilitychange', this._onVisibilityChange)
    window.removeEventListener('pageshow', this._onPageShow)
    this._post({ event: 'tab_closing', tabId: this._tabId })
    this._worker = null
  }
}
